import DefaultLoader from '@app/components/various/loaders/DefaultLoader';
import {
  createSelection,
  getDefaultStockType,
  getIsBuyer,
  getIsOrderSettingsFormDisabled,
  getIsSeller,
  getOpenOrClosedOrder,
  redirectedUserToLatestSelection,
} from '@app/ducks';
import { getIsOrderEditable } from '@app/logic/Orders';
import { paths } from '@app/paths';
import { useSelectionsList } from '@app/services/hooks/orders/useSelectionsList';
import { isDefined } from '@app/utils/is';
import { Order } from '@typings';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';

interface Props {
  getRedirectionUrl: (selectionId: string) => string;
}

export const LastSelectionLoader = ({ getRedirectionUrl }: Props) => {
  const { search } = useLocation();
  const dispatch = useDispatch();
  const isCreatingSelection = useSelector(getIsOrderSettingsFormDisabled);
  const currentOrderData = useSelector(getOpenOrClosedOrder);
  const isBuyer = useSelector(getIsBuyer);
  const isSeller = useSelector(getIsSeller);
  const initialStockType = useSelector(getDefaultStockType);

  const currentOrderId = currentOrderData.order;
  const hasActiveOrderInStore = isDefined(currentOrderId) && getIsOrderEditable(currentOrderData.status);
  const { isLoading: isLoadingFirstSelection, data: { orders: openSelections = [] } = {} } = useSelectionsList({
    data: { limit: 1, search: '', statuses: ['open' as Order.SelectionStatus] },
    isEnabled: !hasActiveOrderInStore,
  });

  const [firstOpenSelection] = openSelections;
  const shouldRedirectUserToLatestSelection = isDefined(firstOpenSelection);
  const hasOpenSelection = isDefined(firstOpenSelection);

  React.useEffect(() => {
    if (!shouldRedirectUserToLatestSelection) {
      return;
    }

    dispatch(redirectedUserToLatestSelection());
  }, [dispatch, shouldRedirectUserToLatestSelection]);

  const shouldCreateNewSelection = isBuyer && !hasOpenSelection && !isLoadingFirstSelection && !isCreatingSelection;

  React.useEffect(() => {
    if (!shouldCreateNewSelection) {
      return;
    }

    dispatch(createSelection({ initialStockType, name: '' }));
  }, [dispatch, initialStockType, shouldCreateNewSelection]);

  if (isLoadingFirstSelection || isCreatingSelection) {
    return <DefaultLoader overlay />;
  }

  if (hasActiveOrderInStore) {
    return <Navigate to={`${getRedirectionUrl(currentOrderId)}${search}`} replace />;
  }

  if (hasOpenSelection) {
    return <Navigate to={`${getRedirectionUrl(firstOpenSelection.order)}${search}`} replace />;
  }

  if (isSeller) {
    return <Navigate to={paths.SELECTIONS} replace />;
  }

  return <DefaultLoader overlay />;
};
